import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck, EyeOff, Play, ScrollText } from 'lucide-react'
import {
  Card, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, abandonStockCount, fetchCountSheet, fetchStockCounts, fetchStockLevels,
  formatCount, formatQuantity, may, nameIn, parseQuantity, saveCountLines,
  startStockCount, submitStockCount, translateError, unitIn,
  type CountRow, type CountSheetView, type StockCountState,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { CountVariance } from './CountVariance'

/* ============================================================================
   W-105 · PHYSICAL COUNT — and the index that gets you to one.

   THE COUNT IS BLIND, AND THAT IS THE WHOLE DESIGN. Pack 3 states the rule and the
   reason in one breath: "showing the expected quantity turns a count into a
   confirmation — people see 412, count roughly 412, and move on."

   The expected column is DRAWN and reads "Hidden", which is not the same as leaving it
   out. A missing column looks like a screen that forgot; a column that says "Hidden"
   tells the person counting that the answer exists, that the system is not going to
   show it to them, and — by implication — that guessing is pointless. The blindness is
   the feature, so it is visible.

   AND THE FIGURE IS NOT ON THIS SCREEN TO HIDE. The server does not send it. A screen
   that receives the number and declines to draw it is one devtools window, one CSS
   mistake or one well-meaning "show expected" toggle away from being useless.

   THE INDEX IS OURS. Pack 3 draws a count that is already open and does not draw the
   list you reach it from — reasonably, since a store keeper walking to the linen store
   has one in mind. But a count can be paused, picked up after lunch by somebody else,
   or left open by somebody who went home, so there has to be a way back to it.
   Change Note 04.
   ========================================================================= */

export function StockCounts({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [openCountId, setOpenCountId] = useState<string | null>(null)

  if (!may('stock.move') && !may('stock.adjust')) return <NoPermissionState />

  if (openCountId !== null)
    return (
      <OneCount
        countId={openCountId}
        propertyId={propertyId}
        onClose={() => setOpenCountId(null)}
      />
    )

  return <CountIndex propertyId={propertyId} onOpen={setOpenCountId} locale={locale} t={t} />
}

/* ── the index ─────────────────────────────────────────────────────────────── */

function CountIndex({
  propertyId, onOpen, locale, t,
}: {
  propertyId: string
  onOpen: (id: string) => void
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)

  const counts = useQuery({
    queryKey: ['stock-counts', propertyId, reload],
    queryFn: ({ signal }) => fetchStockCounts(propertyId, { signal }),
    retry: false,
  })

  /* The locations come from the stock LEVELS, the same source W-100 derives its filter
     chips from. ONE NUMBER, ONE SOURCE applied to a list: a second endpoint could offer
     a location the board has no rows for, and starting a count somewhere that shows
     nothing reads as a broken screen rather than as an empty pantry. */
  const levels = useQuery({
    queryKey: ['stock-levels', propertyId],
    queryFn: ({ signal }) => fetchStockLevels(propertyId, { signal }),
    retry: false,
  })

  const locations = useMemo(() => {
    const seen = new Map<string, string>()
    for (const row of levels.data ?? [])
      if (!seen.has(row.stockLocationId))
        seen.set(row.stockLocationId, nameIn(locale, row.locationName, row.locationNameAr))
    return [...seen.entries()]
  }, [levels.data, locale])

  /* WHICH LOCATIONS ARE ALREADY BEING COUNTED, derived from the rows below rather than
     asked for separately — and the button disappears for those. Offering "count the
     main store" beside a row that says the main store is being counted is offering
     somebody an action the server will refuse. */
  const busyLocations = useMemo(
    () => new Set((counts.data ?? [])
      .filter((c) => c.state === 'OPEN' || c.state === 'SUBMITTED')
      .map((c) => c.stockLocationId)),
    [counts.data])

  async function start(locationId: string) {
    setBusy(locationId); setError(null)
    try {
      const { stockCountId } = await startStockCount(propertyId, { stockLocationId: locationId })
      onOpen(stockCountId)
    } catch (caught: unknown) {
      setError(caught)
      setReload((n) => n + 1)
    } finally {
      setBusy(null)
    }
  }

  if (counts.isLoading && !counts.error) return <LoadingState rows={5} />

  if (counts.error && !counts.data) {
    if (counts.error instanceof ApiError && counts.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(counts.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={counts.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {error !== null && <Problem error={error} locale={locale} />}

      {may('stock.move') && (
        <Card>
          {/* NOT THE SCREEN'S OWN NAME. The shell says "Stock counts" directly above. */}
          <CardHeader title={t.cntIndexTitle} icon={<Play className="size-4" />} />
          <div className="flex flex-col gap-3 p-3.5">
            <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
              {t.cntIndexIntro}
            </p>
            {locations.length === 0 ? (
              <p className="text-[12.5px] text-muted-foreground">{t.cntNoLocations}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {locations
                  .filter(([id]) => !busyLocations.has(id))
                  .map(([id, name]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => void start(id)}
                      disabled={busy !== null}
                      className="inline-flex items-center gap-1.5 rounded-hk border border-border
                                 px-3 py-1.5 text-xs font-medium hover:bg-muted
                                 disabled:opacity-60 cursor-pointer"
                    >
                      <ClipboardCheck className="size-3.5" />
                      <bdi>
                        {busy === id
                          ? t.cntStarting
                          : t.cntStartAt.replace('{location}', name)}
                      </bdi>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader title={t.cntRecent} icon={<ScrollText className="size-4" />} />
        {(counts.data ?? []).length === 0 ? (
          <EmptyState title={t.cntNoCounts} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.cntColReference}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.cntColLocation}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.cntColStarted}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.cntColProgress}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.cntColState}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {(counts.data ?? []).map((row) => (
                  <CountIndexRow
                    key={row.stockCountId}
                    row={row}
                    locale={locale}
                    onOpen={() => onOpen(row.stockCountId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function CountIndexRow({
  row, locale, onOpen,
}: { row: CountRow; locale: 'en' | 'ar'; onOpen: () => void }) {
  const t = STRINGS[locale]
  return (
    <tr className="border-b border-border/60 last:border-0">
      {/* A REFERENCE IS A NAME, NOT A QUANTITY, so it stays in Western digits in both
          languages — the same rule as a room number and a version number. CNT-0034 is
          what somebody writes at the top of a paper sheet and says out loud. */}
      <td className="px-3 py-2 font-medium tnum">{row.reference}</td>
      <td className="px-3 py-2">
        <bdi>{nameIn(locale, row.locationName, row.locationNameAr)}</bdi>
      </td>
      <td className="px-3 py-2 text-muted-foreground">{when(row.startedAtUtc, locale)}</td>
      <td className="px-3 py-2 text-end tnum text-muted-foreground">
        {t.cntProgress
          .replace('{counted}', formatCount(row.itemsCounted, locale))
          .replace('{total}', formatCount(row.itemsOnSheet, locale))}
      </td>
      <td className="px-3 py-2"><StateBadge state={row.state} locale={locale} /></td>
      <td className="px-3 py-2 text-end">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                     font-medium hover:bg-muted cursor-pointer"
        >
          {t.cntOpenIt}
        </button>
      </td>
    </tr>
  )
}

/* ── one count: the sheet while it is open, the variance once it is not ────── */

function OneCount({
  countId, propertyId, onClose,
}: { countId: string; propertyId: string; onClose: () => void }) {
  const { locale } = useLocale()
  const [reload, setReload] = useState(0)

  const sheet = useQuery({
    queryKey: ['count-sheet', countId, reload],
    queryFn: ({ signal }) => fetchCountSheet(countId, { signal }),
    retry: false,
  })

  if (sheet.isLoading && !sheet.error) return <LoadingState rows={8} />

  if (sheet.error && !sheet.data) {
    const translated = translateError(sheet.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={sheet.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }
  if (!sheet.data) return <LoadingState />

  /* A COUNT THAT IS NOT OPEN IS A VARIANCE, NOT A SHEET. The two screens are one
     journey and the state decides which half you are looking at — there is nothing to
     type on a sheet that has been handed over, and a "count" screen full of disabled
     boxes teaches nobody anything. */
  if (sheet.data.state !== 'OPEN')
    return (
      <CountVariance
        countId={countId}
        onClose={onClose}
        onChanged={() => setReload((n) => n + 1)}
      />
    )

  return (
    <CountSheet
      sheet={sheet.data}
      propertyId={propertyId}
      onClose={onClose}
      onChanged={() => setReload((n) => n + 1)}
    />
  )
}

function CountSheet({
  sheet, onClose, onChanged,
}: {
  sheet: CountSheetView
  propertyId: string
  onClose: () => void
  onChanged: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  /* WHAT HAS BEEN TYPED BUT NOT YET SAVED, and copying the server's figures into state
     is deliberate here rather than the mistake it usually is. This screen MEANS to
     freeze: a store keeper stands at a shelf typing fourteen numbers, and a refetch
     that replaced them halfway would be indistinguishable from the application eating
     their work. The keying is on the count and round so that a recount starts clean. */
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<'saving' | 'handing' | null>(null)
  const [said, setSaid] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [abandoning, setAbandoning] = useState(false)

  useEffect(() => {
    const seeded: Record<string, string> = {}
    for (const line of sheet.lines)
      seeded[line.stockItemId] = line.countedQuantity === null
        ? ''
        : formatQuantity(line.countedQuantity, locale)
    setTyped(seeded)
    // Reseeded only when the COUNT or its ROUND changes, never on an ordinary refetch.
  }, [sheet.stockCountId, sheet.round]) // eslint-disable-line react-hooks/exhaustive-deps

  /* What to send: every box with something in it that reads as a number.

     AN EMPTY BOX SENDS NOTHING, AND THAT IS THE POINT. A blank is not a zero — it is an
     item nobody has reached. Sending 0 for every untouched box would report a shortfall
     of the entire pantry and suggest writing all of it off. Somebody who means zero
     types zero. */
  function figures(): { stockItemId: string; countedQuantity: number }[] | 'unreadable' {
    const out: { stockItemId: string; countedQuantity: number }[] = []
    for (const [stockItemId, raw] of Object.entries(typed)) {
      if (raw.trim() === '') continue
      // Accepts ٠١٢٣٤٥٦٧٨٩ as well as 0123456789 — what a keypad emits is not our
      // decision, and a store keeper who types ١٢ and is told to enter a number
      // concludes the application does not speak Arabic.
      const value = parseQuantity(raw)
      if (value === null || value < 0) return 'unreadable'
      out.push({ stockItemId, countedQuantity: value })
    }
    return out
  }

  async function save(): Promise<boolean> {
    const lines = figures()
    if (lines === 'unreadable') {
      setError(new Error(t.cntUnreadable))
      return false
    }
    await saveCountLines(sheet.stockCountId, lines)
    return true
  }

  async function run(what: 'saving' | 'handing') {
    setBusy(what); setError(null); setSaid(null)
    try {
      /* HANDING OVER SAVES FIRST — the same rule W-126's publish needed, and for the
         same reason. A hand-over that sent only what the server already has would
         freeze the LAST SAVED sheet while the person is looking at four figures they
         have just typed, and the variance would be computed from a count that is not
         the one on screen. */
      if (!await save()) return
      if (what === 'handing') await submitStockCount(sheet.stockCountId)
      else setSaid(t.cntSaved)
      onChanged()
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function abandon(reason: string) {
    setBusy('saving'); setError(null)
    try {
      await abandonStockCount(sheet.stockCountId, reason)
      setAbandoning(false)
      onClose()
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  const countedNow = Object.values(typed).filter((v) => v.trim() !== '').length
  const location = nameIn(locale, sheet.locationName, sheet.locationNameAr)

  return (
    <div className="flex flex-col gap-3.5">
      <BackBar onClose={onClose} label={t.cntBack} />

      {error !== null && <Problem error={error} locale={locale} />}

      <Card>
        <CardHeader
          title={t.cntSheetTitle.replace('{reference}', sheet.reference)}
          subtitle={sheet.round > 1
            ? `${location} · ${t.cntRound.replace('{round}', String(sheet.round))}`
            : location}
          icon={<ClipboardCheck className="size-4" />}
        />

        <div className="flex flex-col gap-3 p-3.5">
          {/* THE BLINDNESS, SAID OUT LOUD. A person who does not know the expected
              figure is being withheld assumes the screen is broken and goes looking for
              it — in the stock board, which would tell them. */}
          <div
            className="flex items-start gap-2 rounded-hk border border-border bg-muted/40
                       px-3 py-2 text-[12.5px] leading-relaxed"
          >
            <EyeOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            {/* TWO LINES, NOT ONE. Rendered inline they ran together — "This is a
                blind count The expected quantity is not shown" — which reads as a
                missing full stop and made a deliberate rule look like a typo. */}
            <span>
              <strong className="block font-medium">{t.cntBlindTitle}</strong>
              <span className="text-muted-foreground">{t.cntBlindWhy}</span>
            </span>
          </div>

          <p className="text-[12px] text-muted-foreground">
            <bdi>{t.cntHeldNotice.replace('{location}', location)}</bdi>
          </p>

          <div className="text-[12.5px] font-medium">
            {t.cntProgress
              .replace('{counted}', formatCount(countedNow, locale))
              .replace('{total}', formatCount(sheet.itemsOnSheet, locale))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                <th className="px-3 py-2 text-start font-medium">{t.cntColItem}</th>
                <th className="px-3 py-2 text-end font-medium">{t.cntColCounted}</th>
                <th className="px-3 py-2 text-start font-medium">{t.cntColCountedBy}</th>
                <th className="px-3 py-2 text-start font-medium">{t.cntColTime}</th>
                <th className="px-3 py-2 text-end font-medium">{t.cntColExpected}</th>
              </tr>
            </thead>
            <tbody>
              {sheet.lines.map((line) => (
                <tr key={line.stockItemId} className="border-b border-border/60 last:border-0">
                  <td className="px-3 py-2">
                    <bdi className="font-medium">
                      {nameIn(locale, line.itemName, line.itemNameAr)}
                    </bdi>
                    <span className="ms-2 text-[11px] text-muted-foreground">{unitIn(locale, line.unit)}</span>
                  </td>
                  <td className="px-3 py-2 text-end">
                    {/* NEVER type="number". A spinner invites a nudge that changes a
                        counted figure by one without anybody typing, and Arabic digits
                        are refused outright by some browsers' numeric inputs. */}
                    <input
                      type="text"
                      inputMode="numeric"
                      value={typed[line.stockItemId] ?? ''}
                      onChange={(e) => setTyped((prev) => ({
                        ...prev, [line.stockItemId]: e.target.value,
                      }))}
                      aria-label={nameIn(locale, line.itemName, line.itemNameAr)}
                      className="w-20 rounded-hk border border-border bg-background px-2 py-1
                                 text-end text-[13px] tnum"
                    />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {line.countedByName
                      ? <bdi>{nameIn(locale, line.countedByName, line.countedByNameAr)}</bdi>
                      : <span className="text-muted-foreground/70">—</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground tnum">
                    {line.countedAtUtc ? clock(line.countedAtUtc, locale) : '—'}
                  </td>
                  {/* THE COLUMN IS DRAWN AND IT SAYS "HIDDEN". A missing column reads as
                      a screen that forgot; this one says the answer exists and is being
                      withheld on purpose, which is the message. */}
                  {/* NEITHER `uppercase` NOR `tracking-wide` HERE. Letter-spacing
                      breaks the joins between Arabic letters, turning مخفي into four
                      disconnected shapes, and uppercase does nothing to Arabic at all —
                      so both were styling that worked in one language and damaged the
                      other. */}
                  <td className="px-3 py-2 text-end text-[11.5px] italic
                                 text-muted-foreground/70">
                    {t.cntHidden}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 border-t border-border p-3.5">
          <button
            type="button"
            onClick={() => void run('saving')}
            disabled={busy !== null}
            className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                       hover:bg-muted disabled:opacity-60 cursor-pointer"
          >
            {busy === 'saving' ? t.cntSaving : t.cntSave}
          </button>
          <button
            type="button"
            onClick={() => void run('handing')}
            disabled={busy !== null || countedNow === 0}
            className="rounded-hk bg-[var(--hk-primary)] px-3 py-1.5 text-xs font-medium
                       text-white disabled:opacity-60 cursor-pointer"
          >
            {busy === 'handing' ? t.cntHandingOver : t.cntHandOver}
          </button>
          {said !== null && (
            <span role="status" className="text-[12px] text-muted-foreground">{said}</span>
          )}
          <button
            type="button"
            onClick={() => setAbandoning(true)}
            disabled={busy !== null}
            className="ms-auto text-[11.5px] text-muted-foreground underline
                       underline-offset-2 disabled:opacity-60 cursor-pointer"
          >
            {t.cntAbandon}
          </button>
        </div>

        {abandoning && (
          <ReasonForm
            label={t.cntAbandonReason}
            confirm={t.cntAbandonConfirm}
            busy={busy !== null}
            onCancel={() => setAbandoning(false)}
            onSubmit={(reason) => void abandon(reason)}
          />
        )}
      </Card>
    </div>
  )
}

/* ── shared furniture ──────────────────────────────────────────────────────── */

export function BackBar({ onClose, label }: { onClose: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className="self-start rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                 font-medium hover:bg-muted cursor-pointer"
    >
      {label}
    </button>
  )
}

export function ReasonForm({
  label, confirm, busy, onCancel, onSubmit,
}: {
  label: string
  confirm: string
  busy: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [reason, setReason] = useState('')

  return (
    <div className="flex flex-col gap-2 border-t border-border p-3.5">
      <label className="text-[12px] font-medium" htmlFor="count-reason">{label}</label>
      <textarea
        id="count-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        /* ALWAYS rtl, WHATEVER LANGUAGE THE SCREEN IS IN. An English-speaking manager
           typing an Arabic reason into a left-aligned box cannot tell where the words
           have gone. Same rule as W-126's Arabic label boxes. */
        dir="rtl"
        className="rounded-hk border border-border bg-background px-2.5 py-1.5 text-[13px]"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSubmit(reason)}
          disabled={busy || reason.trim() === ''}
          className="rounded-hk bg-[var(--hk-primary)] px-3 py-1.5 text-xs font-medium
                     text-white disabled:opacity-60 cursor-pointer"
        >
          {confirm}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                     hover:bg-muted cursor-pointer"
        >
          {t.cntCancel}
        </button>
      </div>
    </div>
  )
}

export function StateBadge({
  state, locale,
}: { state: StockCountState; locale: 'en' | 'ar' }) {
  const t = STRINGS[locale]
  const [label, tone] =
    state === 'OPEN' ? [t.cntOpen, 'border-[#93c5fd] bg-[#eff6ff] text-[#1d4ed8] dark:border-[#1e40af] dark:bg-[#0f1c33] dark:text-[#93c5fd]']
    : state === 'SUBMITTED' ? [t.cntSubmitted, 'border-[#fcd34d] bg-[#fffbeb] text-[#b45309] dark:border-[#92400e] dark:bg-[#2a1f07] dark:text-[#fbbf24]']
    : state === 'APPROVED' ? [t.cntApproved, 'border-[#86efac] bg-[#f0fdf4] text-[#15803d] dark:border-[#166534] dark:bg-[#0d1f13] dark:text-[#86efac]']
    : [t.cntAbandoned, 'border-border bg-muted text-muted-foreground']

  return (
    <span className={`inline-block rounded-hk border px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {label}
    </span>
  )
}

export function Problem({ error, locale }: { error: unknown; locale: 'en' | 'ar' }) {
  const translated = translateError(error, locale)
  return (
    <div
      role="alert"
      className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12.5px]
                 text-[#b91c1c] dark:border-[#7f1d1d] dark:bg-[#2a1212] dark:text-[#fca5a5]"
    >
      {translated.message}
    </div>
  )
}

export function when(iso: string, locale: 'en' | 'ar'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso))
}

export function clock(iso: string, locale: 'en' | 'ar'): string {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-IQ' : 'en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso))
}
